import { useId, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { NewInput } from '../../PIM/common/NewInput';
import { ChevronIcon } from '../../../bombiix-icon';
import type { CmTimePickerProps, TimeParts, TimePeriod, TimeUnitColumnProps } from './cm-time-picker.types';
import './field-control.css';

/**
 * Custom Module time picker (Figma "Select Column Date/Time Picker"): a read-only input that opens a
 * popover with typeable hour and minute boxes, step chevrons, an AM/PM switch and a Set Time button.
 * The stored value stays 24-hour HH:mm.
 * @param id - Id for the trigger input.
 * @param value - Current time as HH:mm, or null.
 * @param onChange - Called with the chosen HH:mm when Set Time is pressed.
 * @param disabled - Whether the picker is locked.
 * @returns The time picker.
 */
function CmTimePicker({ id, value, onChange, disabled = false }: CmTimePickerProps) {
  const { t } = useTranslation('custom-module-system-canvas');
  const popoverId = useId();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TimeParts>(() => parseTime(value));
  const periodLabels: Record<TimePeriod, string> = { AM: t(PERIOD_KEYS.AM), PM: t(PERIOD_KEYS.PM) };
  const displayValue = value ? formatTime(parseTime(value), periodLabels) : '';

  const handleToggle = (nextOpen: boolean) => {
    if (disabled) return;
    if (nextOpen) setDraft(parseTime(value));
    setOpen(nextOpen);
  };

  const commitHour = () => setDraft((prev) => ({ ...prev, hour: clampUnit(prev.hour, HOUR_MIN, HOUR_MAX) }));

  const commitMinute = () => setDraft((prev) => ({ ...prev, minute: clampUnit(prev.minute, MINUTE_MIN, MINUTE_MAX) }));

  const handleSetTime = () => {
    onChange(toTime24({ ...draft, hour: clampUnit(draft.hour, HOUR_MIN, HOUR_MAX), minute: clampUnit(draft.minute, MINUTE_MIN, MINUTE_MAX) }));
    setOpen(false);
  };

  const popover = (
    <Popover id={popoverId} className="cm-time-picker">
      <Popover.Content className="cm-time-picker__body">
        <span className="cm-time-picker__title">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.TITLE')}</span>
        <hr className="cm-time-picker__divider" />
        <div className="cm-time-picker__units">
          <TimeUnitColumn
            value={draft.hour}
            label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.HOUR')}
            increaseLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.INCREASE_HOUR')}
            decreaseLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.DECREASE_HOUR')}
            onType={(hour) => setDraft((prev) => ({ ...prev, hour }))}
            onStep={(delta) => setDraft((prev) => ({ ...prev, hour: stepUnit(prev.hour, delta, HOUR_MIN, HOUR_MAX) }))}
            onCommit={commitHour}
          />
          <span className="cm-time-picker__separator">:</span>
          <TimeUnitColumn
            value={draft.minute}
            label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.MINUTE')}
            increaseLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.INCREASE_MINUTE')}
            decreaseLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.DECREASE_MINUTE')}
            onType={(minute) => setDraft((prev) => ({ ...prev, minute }))}
            onStep={(delta) => setDraft((prev) => ({ ...prev, minute: stepUnit(prev.minute, delta, MINUTE_MIN, MINUTE_MAX) }))}
            onCommit={commitMinute}
          />
        </div>
        <div className="cm-time-picker__periods" role="group">
          {PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              className={`cm-time-picker__period${draft.period === period ? ' cm-time-picker__period--active' : ''}`}
              aria-pressed={draft.period === period}
              onClick={() => setDraft((prev) => ({ ...prev, period }))}
            >
              {periodLabels[period]}
            </button>
          ))}
        </div>
        <hr className="cm-time-picker__divider" />
        <button type="button" className="cm-time-picker__set" onClick={handleSetTime}>
          {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.SET_TIME')}
        </button>
      </Popover.Content>
    </Popover>
  );

  return (
    <OverlayTrigger trigger="click" placement="bottom-start" rootClose show={open} onToggle={handleToggle} overlay={popover}>
      <NewInput
        id={id}
        type="text"
        readOnly
        className="cm-time-picker__trigger"
        placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.SELECT_TIME')}
        value={displayValue}
        disabled={disabled}
      />
    </OverlayTrigger>
  );
}

export default CmTimePicker;

/**
 * One hour or minute column: an up chevron, a box the user can type digits into, and a down chevron.
 * Arrow Up / Down step the value; leaving the box clamps it into range.
 * @param props - See TimeUnitColumnProps.
 * @returns The column.
 */
function TimeUnitColumn({ value, label, increaseLabel, decreaseLabel, onType, onStep, onCommit }: TimeUnitColumnProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      onStep(event.key === 'ArrowUp' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onCommit();
    }
  };

  return (
    <div className="cm-time-picker__unit">
      <button type="button" className="cm-time-picker__step cm-time-picker__step--up" aria-label={increaseLabel} onClick={() => onStep(1)}>
        <ChevronIcon size={CHEVRON_SIZE} />
      </button>
      <input
        type="text"
        inputMode="numeric"
        maxLength={UNIT_MAX_LENGTH}
        className="cm-time-picker__box"
        aria-label={label}
        value={value}
        onChange={(event) => onType(event.target.value.replace(/\D/g, ''))}
        onFocus={(event) => event.target.select()}
        onBlur={onCommit}
        onKeyDown={handleKeyDown}
      />
      <button type="button" className="cm-time-picker__step" aria-label={decreaseLabel} onClick={() => onStep(-1)}>
        <ChevronIcon size={CHEVRON_SIZE} />
      </button>
    </div>
  );
}

/**
 * Splits a stored HH:mm value into 12-hour picker parts; anything unreadable starts at 12:00 AM.
 * @param value - Stored 24-hour time.
 * @returns Hour (01-12), minute (00-59) and period.
 */
function parseTime(value: string | null | undefined): TimeParts {
  const match = TIME_PATTERN.exec(value ?? '');
  if (!match) return { hour: padUnit(HOUR_MAX), minute: padUnit(MINUTE_MIN), period: 'AM' };
  const hour24 = Math.min(Number(match[1]), HOURS_PER_DAY - 1);
  const minute = Math.min(Number(match[2]), MINUTE_MAX);
  return { hour: padUnit(hour24 % HOURS_PER_PERIOD || HOURS_PER_PERIOD), minute: padUnit(minute), period: hour24 >= HOURS_PER_PERIOD ? 'PM' : 'AM' };
}

/**
 * Turns picker parts back into the stored 24-hour HH:mm value.
 * @param parts - Hour, minute and period from the picker.
 * @returns The time as HH:mm.
 */
function toTime24(parts: TimeParts): string {
  const hour24 = (Number(parts.hour) % HOURS_PER_PERIOD) + (parts.period === 'PM' ? HOURS_PER_PERIOD : 0);
  return `${padUnit(hour24)}:${padUnit(Number(parts.minute))}`;
}

/**
 * Formats picker parts for the trigger input, e.g. "09:30 AM".
 * @param parts - Hour, minute and period.
 * @param periodLabels - Translated AM / PM labels.
 * @returns The display text.
 */
function formatTime(parts: TimeParts, periodLabels: Record<TimePeriod, string>): string {
  return `${parts.hour}:${parts.minute} ${periodLabels[parts.period]}`;
}

/**
 * Pulls a typed hour or minute into range and pads it to two digits; an empty box falls back to the minimum.
 * @param text - Typed digits.
 * @param min - Lowest allowed value.
 * @param max - Highest allowed value.
 * @returns The clamped, padded value.
 */
function clampUnit(text: string, min: number, max: number): string {
  const typed = text ? Number(text) : min;
  return padUnit(Math.min(Math.max(typed, min), max));
}

/**
 * Moves an hour or minute one step, wrapping past either end of its range.
 * @param text - Current value.
 * @param delta - +1 or -1.
 * @param min - Lowest allowed value.
 * @param max - Highest allowed value.
 * @returns The stepped, padded value.
 */
function stepUnit(text: string, delta: number, min: number, max: number): string {
  const span = max - min + 1;
  const current = Number(clampUnit(text, min, max));
  return padUnit(((((current - min + delta) % span) + span) % span) + min);
}

/**
 * Pads a number to two digits.
 * @param unit - Hour or minute.
 * @returns The two-digit text.
 */
function padUnit(unit: number): string {
  return String(unit).padStart(UNIT_MAX_LENGTH, '0');
}

const TIME_PATTERN = /^(\d{1,2}):(\d{2})/;

const PERIODS: TimePeriod[] = ['AM', 'PM'];

const PERIOD_KEYS: Record<TimePeriod, string> = {
  AM: 'CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.AM',
  PM: 'CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TIME_PICKER.PM',
};

const HOUR_MIN = 1;

const HOUR_MAX = 12;

const MINUTE_MIN = 0;

const MINUTE_MAX = 59;

const HOURS_PER_PERIOD = 12;

const HOURS_PER_DAY = 24;

const UNIT_MAX_LENGTH = 2;

const CHEVRON_SIZE = 24;
